import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/components/ui/badge';
import { ImportHistoryItem } from '../../models/ImportHistoryModel';
import { ImportHistoryActions } from '../interaction/ImportHistoryActions';
import { cn } from '@shared/utils/utils';

export interface ImportHistoryCardProps extends RenderItemProps<ImportHistoryItem> {
  onViewDetails: (item: ImportHistoryItem) => void;
  onRetry: (item: ImportHistoryItem) => void;
}

export const ImportHistoryCard = ({ item, onViewDetails, onRetry }: ImportHistoryCardProps) => {
  const { t, i18n } = useTranslation('shopsModule');

  const status = item.status;
  const colorMap: Record<string, string> = {
    imported: 'bg-bg-success text-success dark:text-bright-success',
    duplicated: 'bg-bg-warning text-warning',
    skipped: 'bg-body-bg text-text-secondary border border-card-border/50',
    failed: 'bg-bg-error text-error',
  };

  const labelMap: Record<string, string> = {
    imported: t('importHistory.status.imported', 'Importado'),
    duplicated: t('importHistory.status.duplicated', 'Duplicado'),
    skipped: t('importHistory.status.skipped', 'Omitido'),
    failed: t('importHistory.status.failed', 'Error'),
  };

  const colorClass = colorMap[status] || colorMap.failed;
  const label = labelMap[status] || labelMap.failed;

  const formattedDate = new Date(item.imported_at).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="p-4 border border-card-border rounded-lg bg-card-bg hover:bg-hover-press transition-colors mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-col">
          <span className="text-xs text-text-secondary font-medium">
            {t('importHistory.table.id', 'ID')}: {item.id}
          </span>
          <span className="text-sm font-semibold text-title truncate max-w-[200px]" title={item.external_order_ref}>
            {t('importHistory.table.externalRef', 'Referencia')}: {item.external_order_ref.replace(/^#/, '')}
          </span>
        </div>
        <ImportHistoryActions item={item} onViewDetails={onViewDetails} onRetry={onRetry} />
      </div>

      <div className="flex justify-between items-center">
        <Badge className={cn('text-[10px] px-2 py-0.5 font-medium border-0 shadow-none', colorClass)}>{label}</Badge>
        <div className="text-[11px] text-text-secondary">{formattedDate}</div>
      </div>

      {(item.reason || item.status === 'skipped') && (
        <div className="mt-2 text-xs text-text-secondary italic">
          {item.status === 'skipped' && item.reason === 'VALIDATION_ERROR'
            ? t('importHistory.reasons.skippedByFilters', 'Omitido por filtros')
            : item.status === 'duplicated' && item.reason === 'ALREADY_EXISTS'
              ? t('importHistory.reasons.alreadyExists', 'Ya existe')
              : item.status === 'failed' && item.reason === 'EXCEPTION'
                ? t('importHistory.reasons.failedByException', 'Error en proceso')
                : item.reason || '-'}
        </div>
      )}
    </div>
  );
};
